import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemDescriptionItem} from '@github-ui/list-view/ListItemDescriptionItem'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {VerifiedIcon, PackageIcon, CloudIcon} from '@primer/octicons-react'
import {artifactRegistryShowRoute} from '../routes/artifact-registry-route'
import type {Artifact} from '../types'
import {artifactIsDetectedBy} from '../types'
import {Label, LabelGroup, Link, RelativeTime, IconButton} from '@primer/react'
import styles from './ArtifactListItem.module.css'
import {repositoryPath} from '@github-ui/paths'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'

type ArtifactListItemProps = {
  artifact: Artifact
  org: string
  isV2: boolean
}

export function ArtifactListItem({artifact, org, isV2}: ArtifactListItemProps) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const interpunct = ' · '
  const hasLastUpdated = artifact.updated_at && artifact.updated_at !== artifact.created_at
  const detectedBy = artifactIsDetectedBy(artifact)

  const detectionDateDisplay = isV2 ? (
    <RelativeTime
      format="datetime"
      datetime={artifact.created_at}
      second="2-digit"
      minute="2-digit"
      hour="2-digit"
      weekday={undefined}
      year="numeric"
    />
  ) : (
    new Date(artifact.created_at).toLocaleDateString('default', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    })
  )

  const leadingIcon = artifact.attestation_id ? (
    <VerifiedIcon size={16} className={'mr-2 fgColor-success'} verticalAlign={'middle'} />
  ) : (
    <PackageIcon size={16} className={'mr-2 fgColor-muted'} verticalAlign={'middle'} />
  )

  // Build accessible title that includes deployment count
  const accessibleTitle =
    typeof artifact.deployment_count === 'number' && artifact.deployment_count > 0
      ? `${artifact.artifact_name}, ${artifact.deployment_count} ${
          artifact.deployment_count === 1 ? 'deployment' : 'deployments'
        }`
      : artifact.artifact_name

  // Build tooltip text for deployment count
  const deploymentTooltipText =
    typeof artifact.deployment_count === 'number' && artifact.deployment_count > 0
      ? `${artifact.deployment_count} active ${artifact.deployment_count === 1 ? 'deployment' : 'deployments'}`
      : ''

  return (
    <ListItem
      title={
        <ListItemTitle
          href={artifactRegistryShowRoute.generatePath({org, id: artifact.artifact_metadata_id.toString()})}
          value={artifact.artifact_name}
          aria-label={accessibleTitle}
        >
          {artifact.version && <span className="fgColor-muted f4 text-light ml-2">{artifact.version}</span>}
        </ListItemTitle>
      }
      metadata={
        <ListItemMetadata>
          {typeof artifact.deployment_count === 'number' && artifact.deployment_count > 0 && (
            <>
              <IconButton
                icon={CloudIcon}
                variant="invisible"
                inactive
                aria-label={deploymentTooltipText}
                className={styles.DeploymentIcon}
                tabIndex={-1}
              />
              <span aria-hidden="true">{artifact.deployment_count}</span>
            </>
          )}
        </ListItemMetadata>
      }
    >
      <ListItemLeadingContent>
        <ListItemLeadingVisual>{leadingIcon}</ListItemLeadingVisual>
      </ListItemLeadingContent>
      <ListItemMainContent>
        {artifact.tags && artifact.tags.length > 0 && (
          <LabelGroup className="my-1">
            {artifact.tags.map(tag => (
              <Label key={`${artifact.artifact_metadata_id}-${tag}`} className={styles.TagLabel} size="small">
                {tag}
              </Label>
            ))}
          </LabelGroup>
        )}
        <ListItemDescription>
          {artifact.repository_name && (
            <ListItemDescriptionItem>
              <Link
                href={repositoryPath({owner: org, repo: artifact.repository_name})}
                {...getHovercardAttributesForType('repository', {owner: org, repo: artifact.repository_name})}
                muted
                suppressHydrationWarning
              >
                {artifact.repository_name}
              </Link>
            </ListItemDescriptionItem>
          )}

          {detectedBy ? (
            <ListItemDescriptionItem>
              {interpunct} Detected by {detectedBy} on {detectionDateDisplay}
            </ListItemDescriptionItem>
          ) : (
            <ListItemDescriptionItem>
              {interpunct} Detected on {detectionDateDisplay}
            </ListItemDescriptionItem>
          )}

          {hasLastUpdated && (
            <ListItemDescriptionItem>
              {interpunct} Updated <RelativeTime datetime={artifact.updated_at} />
            </ListItemDescriptionItem>
          )}
        </ListItemDescription>
      </ListItemMainContent>
    </ListItem>
  )
}
